import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import {
  Button,
  Modal,
  Spin,
  Table
} from 'antd';
import Chart from 'chart.js/auto';
import { Pie } from 'react-chartjs-2';
import {
  notificationsAtom,
  graphsDateRangeAtom
} from '../../../../../atoms/Atoms';
import {
  getBatteryBrandColors,
  thousand,
} from '../../../../../functions';
import api from '../../../../../api/api';
import envConfig from '../../../../../envConfig';

function BatteryBrandsGraph({ orgReferralCode, closerId }) {
  const orgCode = orgReferralCode;  
  const { t } = useTranslation();
  const [graphsDateRange, setGraphsDateRange] = useRecoilState(graphsDateRangeAtom);
  const chartRef = useRef();
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [showTable, setShowTable] = useState(false);

  function getData() {
    if (!graphsDateRange.start || !graphsDateRange.end) {
      return;
    }
    setLoading(true);
    const startDate = dayjs(graphsDateRange.start);
    const endDate = dayjs(graphsDateRange.end);
    const startString = startDate.format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    api.get(`reports/top/batterybrand?startDate=${startString}&endDate=${endString}${orgCode ? `&orgReferralCode=${orgCode}` : ''}${closerId ? `&closerId=${closerId}` : ''}`)
    .then((response) => {
      setLoading(false);
      if (response.data.success) {
        if (response.data.data?.length > 0) {
          setReportData(response.data.data);
        } else {
          setReportData([{
            label: 'N/A',
            count: 0
          }]);
        }
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          },
        ]);
      }
    })
    .catch((error) => {
      setLoading(false);
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: t('error_getting_data'),
          error: true,
        },
      ]);
    });
  }

  useEffect(() => {
    if (graphsDateRange.reload) {
      getData();
      setGraphsDateRange({
        ...graphsDateRange,
        reload: false
      });
    }
  }, [graphsDateRange.reload]);

  useEffect(() => {
    getData();
  }, []);

  return (
    <div className="border-2 shadow-lg pb-20 pt-4">
      <Spin spinning={loading}>
        <div className="text-center" style={{ height: 400 }}>
          <p className="font-bold">{t('top_battery_brands')}</p>
          <Pie
            ref={chartRef}
            data={{
              labels: _.map(reportData, (s) => s.label),
              datasets: [
                {
                  label: t('count'),
                  backgroundColor: getBatteryBrandColors(reportData),
                  borderWidth: 1,
                  data: _.map(reportData, (x) => x.count)
                }
              ]
            }}
            options={{
              maintainAspectRatio: false,
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
                title: {
                  display: false,
                },
                datalabels: {
                  color: 'black',
                  formatter: (value, context) => thousand(value),
                  labels: {
                    title: {
                      font: {
                        weight: 'regular'
                      }
                    }
                  }
                }
              },
            }}
          />
          <div className="text-center flex, space-x-2">
            <Button
              type="primary"
              size="small"
              onClick={() => getData()}
              className="mt-4"
            >
              {t('refresh')}
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => setShowTable(true)}
              className="mt-4"
            >
              {t('view_table')}
            </Button>
          </div>
        </div>
      </Spin>
      <Modal
        width="80%"
        title={t('top_battery_brands')}
        open={showTable}
        onCancel={() => setShowTable(false)}
        footer={[]}
      >
        <Table
          columns={[
            {
              title: t('brand'),
              align: 'left',
              className: 'text-sm',
              sorter: (a, b) => a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase()),
              render: (row) => (
                <p>{row.label}</p>
              ),
            },
            {
              title: t('count'),
              align: 'left',
              className: 'text-sm',
              sorter: (a, b) => a.count - b.count,
              render: (row) => (
                <p>{thousand(row.count)}</p>
              ),
            },
          ]}
          dataSource={reportData}
          pagination={false}
          bordered
        />
      </Modal>
    </div>
  );
}

export default BatteryBrandsGraph;
